/**
 * Stale-chunk recovery.
 *
 * After a deploy, JS chunks get new content hashes. A browser still running the
 * previous bundle will try to lazy-load a route chunk whose hash no longer exists
 * → the dynamic import rejects with "Failed to fetch dynamically imported module"
 * (or a sibling message). Left unhandled it either blanks the app or trips the
 * ErrorBoundary. The fix is to reload once so the client pulls fresh index.html +
 * the new chunk names.
 *
 * Recovery is wired in three places (main.tsx window listeners + ErrorBoundary)
 * because these failures surface through different channels:
 *   - Vite's `vite:preloadError` event (imports via the __vitePreload helper)
 *   - a window `error` / `unhandledrejection` (bare import(), deps' own imports)
 *   - a React render throw caught by the ErrorBoundary (React.lazy)
 */

// Covers Chromium ("Failed to fetch dynamically imported module"),
// Firefox ("error loading dynamically imported module"), and
// Safari ("Importing a module script failed").
// Also covers the MIME variant: when the edge answers a missing chunk with the SPA shell (HTTP 200 +
// text/html) instead of a 404, the browser refuses to run it as a module and reports a MIME-type
// error, not a fetch failure. apps/web/worker.js now returns a real 404 so this should not happen —
// but a stale shell hitting a cached soft-200 still self-heals through this arm rather than crashing.
const CHUNK_ERROR_RE =
  /(Failed to fetch|error loading|Importing a module script failed|Unable to preload).*dynamically imported module|dynamically imported module|Importing a module script failed|Loading chunk .* failed|ChunkLoadError|Expected a JavaScript(?: or WebAssembly)? module script|MIME type of ("?)text\/html\1/i

export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false
  if (typeof err === 'string') return CHUNK_ERROR_RE.test(err)
  const msg = (err as { message?: unknown; name?: unknown }).message
  const name = (err as { name?: unknown }).name
  return (
    (typeof msg === 'string' && CHUNK_ERROR_RE.test(msg)) ||
    (typeof name === 'string' && CHUNK_ERROR_RE.test(name))
  )
}

// Time-boxed guard (not a permanent flag): reload at most once per cooldown so a
// genuinely-missing asset can't spin the page, but a stale chunk from a LATER
// deploy still self-heals. Persists across the reload via sessionStorage.
const RELOAD_STAMP_KEY = 'stale-chunk-reloaded-at'
const RELOAD_COOLDOWN_MS = 20_000

/** Reload once to recover from a stale bundle. Returns true if a reload was triggered. */
export function reloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? 0)
    if (Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS) return false
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode / blocked) — fall through and reload anyway.
  }
  window.location.reload()
  return true
}

// ── Proactive prevention ─────────────────────────────────────────────────────
// Rather than only recovering AFTER a chunk 404 (reactive, above), detect that a
// newer deploy is live and reload at the next graceful moment (a route change),
// before the app ever tries to load a chunk that no longer exists.

/** The build this bundle was compiled from (injected by vite.config.ts). */
export const BUILD_ID: string = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'

let newVersionLive = false

/** True once a poll has seen a build ID on the server newer than this bundle's. */
export function isNewVersionAvailable(): boolean {
  return newVersionLive
}

/**
 * Fetch the deployed build id and compare it to this bundle's. Latches `newVersionLive`
 * on mismatch. Never throws — a failed/blocked fetch just reports "no update".
 * Skips entirely for local dev builds (BUILD_ID === 'dev').
 */
export async function checkForNewVersion(): Promise<boolean> {
  if (BUILD_ID === 'dev') return false
  try {
    // Cache-bust + no-store so we read the CDN edge's current version, not a cached copy.
    const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return newVersionLive
    const data = (await res.json()) as { buildId?: string }
    if (data.buildId && data.buildId !== BUILD_ID) newVersionLive = true
    return newVersionLive
  } catch {
    return newVersionLive
  }
}

// ── Fresh-bundle watch ──────────────────────────────────────────────────────
// Long-lived tabs are how people actually browse: they leave the site open and
// come back hours (or deploys) later, still running yesterday's bundle. A
// client-side navigation from that stale entry can mix chunk generations and
// crash React to a white screen (2026-07-20 on schedulesmcp /lanes). This
// watch polls the deployed build id every 5 minutes and reloads at the next
// safe moment: when the tab regains visibility — at most once per minute.
const FRESH_RELOAD_KEY = 'fresh-bundle-reloaded-at'

function reloadForFreshBundle(): void {
  try {
    const last = Number(sessionStorage.getItem(FRESH_RELOAD_KEY) ?? 0)
    if (Number.isFinite(last) && Date.now() - last < 60_000) return
    sessionStorage.setItem(FRESH_RELOAD_KEY, String(Date.now()))
  } catch {
    /* storage blocked — reload anyway */
  }
  window.location.reload()
}

export function startFreshBundleWatch(): void {
  if (BUILD_ID === 'dev') return
  const poll = () => void checkForNewVersion()
  setInterval(poll, 5 * 60_000)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    // Re-check on refocus (the deploy likely happened while the tab slept),
    // then reload if anything newer is live.
    void checkForNewVersion().then((stale) => {
      if (stale) reloadForFreshBundle()
    })
  })
}
